import React from 'react';
import useNow from '../../hooks/useNow';
import { Link } from 'react-router-dom';
import { Box } from 'lucide-react';
import { timeAgo } from '../../hooks/useLatestBlocks';

/**
 * Lista "Latest Blocks" (maqueta "Classic Explorer").
 * Props: blocks, loading, error, title, footerLink (opcional: { to, label })
 */
const LatestBlocks = ({ blocks, loading, error, title = 'Latest Blocks', footerLink, footer, headerRight, emptyMessage, placeholderRows = 6 }) => {
  const now = useNow();

  return (
    <section className="card min-w-0 flex flex-col">
      <div className="card-header">
        <h2 className="card-title m-0">{title}</h2>
        <div className="flex flex-wrap items-center gap-3">
          {error && blocks.length > 0 && <span className="text-xs text-amber-700">Update delayed</span>}
          {headerRight}
        </div>
      </div>

      {loading && blocks.length === 0 ? (
        <div className="animate-pulse">
          {[...Array(placeholderRows)].map((_, i) => (
            <div key={i} className="flex items-center gap-3.5 px-5 py-3.5 border-b border-gray-100">
              <div className="w-11 h-11 rounded-lg bg-gray-100" />
              <div className="flex-1 space-y-2"><div className="h-3 w-28 bg-gray-200 rounded" /><div className="h-3 w-16 bg-gray-100 rounded" /></div>
              <div className="flex-1 space-y-2"><div className="h-3 w-32 bg-gray-200 rounded" /><div className="h-3 w-20 bg-gray-100 rounded" /></div>
            </div>
          ))}
        </div>
      ) : blocks.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-gray-500">{error || emptyMessage || 'No blocks to show.'}</p>
      ) : (
        <ul className="flex-1">
          {blocks.map((b) => (
            <li key={b.num} className="flex items-center gap-3.5 px-5 py-3.5 border-b border-gray-100">
              <div aria-hidden="true" className="w-11 h-11 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                <Box size={20} strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/block/${b.num}`} className="link font-mono text-sm">{b.num.toLocaleString('en-US')}</Link>
                <div className="text-xs text-gray-500">{timeAgo(b.timestamp, now)}</div>
              </div>
              <div className="flex-1 min-w-0 text-sm">
                <div className="truncate">
                  Producer {b.producer ? <Link to={`/account-detail/${b.producer}`} className="link">{b.producer}</Link> : <span className="text-gray-400">—</span>}
                </div>
                <div className="text-xs text-gray-500">
                  {b.trxCount != null
                    ? `${b.trxCount} transaction${b.trxCount === 1 ? '' : 's'}`
                    : b.blockInTurn != null ? `block ${b.blockInTurn}/12 of its turn` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {footer}
      {footerLink && <Link to={footerLink.to} className="card-footer-link mt-auto">{footerLink.label}</Link>}
    </section>
  );
};

export default React.memo(LatestBlocks);
